import { produce } from "immer";
import { mergeDeepRight } from "ramda";

import { type FundExplorerDataIndexed } from "@hsl/fund-explorer/types";

import type {
    AudienceConfig,
    ColumnConfig,
    Config,
    FilterConfig,
    FilterOverride,
    OptionConfig,
    TabColumnConfig,
    Token,
} from "./types";

/***************************************
 * PROCESS CONFIG
 *****************************************/
export function processConfig(config: Config) {
    return produce(config, (output) => {
        /**
         * FUND CENTRE ID
         */
        if (!config.fundCentreId)
            throw new Error("No fund centre IDs in config");
        const id = config.fundCentreId.filter((idConfig) =>
            idConfig.audiences.includes(config.audienceId),
        )[0]?.id;
        if (!id) throw new Error("No fund centre ID found for this audience");
        output.id = id;
        /**
         * TABS
         */
        if (Array.isArray(config.tabs) && config.tabs.length > 0) {
            output.tabs = config.tabs.map((tab) => {
                const columns = tab.columns
                    ? tab.columns.flatMap(
                          expandIterableColumns.bind(null, config),
                      )
                    : [];
                const columnsMobile = tab.columnsMobile
                    ? tab.columnsMobile.flatMap(
                          expandIterableColumns.bind(null, config),
                      )
                    : [];
                return {
                    ...tab,
                    columns,
                    columnsMobile,
                };
            });
        }
    });
}

function expandIterableColumns(config: Config, column: TabColumnConfig) {
    const colData = config.columns.find((col) => col.key === column.key);
    const labelFromMetadata = ["dis[N]y"].includes(column.key);
    if (!colData) {
        return column;
    }
    const merged = applyColumnOverrides(
        config,
        colData,
        mergeDeepRight(colData, column),
    );

    if (!merged.iterable) {
        return merged;
    }
    const { min = 0, max = 0, step = 0 } = column;
    let list = Array.from(
        { length: Math.floor((max - min) / Math.abs(step)) + 1 },
        (_, i) => min + i * Math.abs(step),
    );
    if (step < 0) {
        list = list.reverse();
    }
    const regExN = /\[\s*N\s*\]/g;
    const regExNExpr = /\[(?<nExpr>.*)\]/g;
    const regExExp = /\{.*\}/g;
    return list.map((n) => {
        const key = merged.key.replace(regExN, n.toString());
        const prop = merged.prop.replace(regExN, n.toString());
        const nExpr = `{${
            regExNExpr.exec(merged.label)?.groups?.nExpr
        }}`.replace("N", n.toString());
        const evaluatedN = evaluateExpression(nExpr);
        const _label = merged.label.replace(
            evaluatedN !== null ? regExNExpr : regExN,
            evaluatedN?.toString() ?? n.toString(),
        );
        const evaluated = evaluateExpression(_label);
        const label = evaluated
            ? _label.replace(regExExp, evaluated.toString())
            : _label;
        return {
            ...merged,
            key,
            prop,
            label: labelFromMetadata ? "metadata" : label,
        };
    });
}

function applyColumnOverrides(
    config: Config,
    colData: ColumnConfig,
    column: TabColumnConfig,
): TabColumnConfig {
    const { jurisdictionId } = config;
    if (!colData.overrides || colData.overrides.length === 0) {
        return column;
    }
    const ret = colData.overrides.reduce((oCol, o) => {
        return o.type === "jurisdiction" && o.value?.includes(jurisdictionId)
            ? { ...oCol, ...o.override }
            : oCol;
    }, column);
    return ret;
}

/***************************************
 * PROCESS FILTERS
 *****************************************/

const filterGuard = (
    f: FilterConfig | false | null | undefined,
): f is FilterConfig => Boolean(f);

export function processFilters(
    filters: FilterConfig[],
    indexedData: FundExplorerDataIndexed,
    audienceConfig: AudienceConfig,
) {
    return filters
        .map((filter) => {
            if (
                Array.isArray(filter.options) &&
                !Array.isArray(filter.overrides) &&
                filter.key === "strategy" &&
                audienceConfig.locale === "en_EN"
            ) {
                return filter;
            }

            const [isDisabled] = matchFilterOverrides(
                "disabled",
                filter.overrides,
                audienceConfig,
            );
            if (isDisabled?.disabled) {
                return false;
            }

            const filterOptions = getFilterOptions(
                filter,
                indexedData,
                audienceConfig,
            );

            const filterLabel = getFilterLabels(filter, audienceConfig);

            return {
                ...filter,
                options: filterOptions,
                label: filterLabel,
            };
        })
        .filter(filterGuard);
}

function getFilterLabels(filter: FilterConfig, audienceConfig: AudienceConfig) {
    const labelOverride = matchFilterOverrides(
        "label",
        filter.overrides,
        audienceConfig,
    )[0]?.label;
    return labelOverride ?? filter.label;
}

function getFilterOptions(
    filter: FilterConfig,
    indexedData: FundExplorerDataIndexed,
    audienceConfig: AudienceConfig,
) {
    const optionsOverrides = matchFilterOverrides(
        "options",
        filter.overrides,
        audienceConfig,
    ).flatMap((x) => x.options);
    const options = filter.prop
        ? Array.from(
              new Set(
                  Object.values(indexedData)
                      .map((datum) => datum.data[filter.prop])
                      .filter((val) => Boolean(val)),
              ),
          )
        : filter.options;

    if (!Array.isArray(options)) {
        console.log(`Options not available for filter: ${filter.label}`);
        return [];
    }

    const optionConfigs = options
        .map((option) => {
            const isString = typeof option === "string";
            const override = optionsOverrides?.find((x) =>
                isString
                    ? x?.value === option
                    : x?.key === (option as OptionConfig).key,
            );
            if (override?.disabled) {
                return undefined;
            }
            return (
                override ??
                (isString ? { label: option, value: option } : option)
            );
        })
        .filter((x) => x !== undefined) as OptionConfig[];

    if (filter.hasOwnProperty("ordering")) {
        return filter?.ordering
            ?.map((label) => optionConfigs.find((x) => x.label === label))
            .filter((x) => x !== undefined) as OptionConfig[];
    }
    return optionConfigs;
}

function matchFilterOverrides(
    key: "options" | "label" | "disabled",
    overrides: FilterOverride[] = [],
    audienceConfig: AudienceConfig,
) {
    return overrides?.filter((override) => {
        let audienceMatch = false;
        switch (override.type) {
            case "*":
                audienceMatch = true;
                break;
            case "audience":
                audienceMatch = override.ids.includes(
                    audienceConfig.audienceId,
                );
                break;
            case "jurisdiction":
                audienceMatch = override.ids.includes(
                    audienceConfig.jurisdictionId,
                );
                break;
            case "language":
                audienceMatch = override.ids.includes(
                    audienceConfig.languageId,
                );
                break;
            case "usertype":
                audienceMatch = override.ids.includes(
                    audienceConfig.usertypeId,
                );
                break;
            default:
                break;
        }
        return audienceMatch && override.hasOwnProperty(key);
    });
}

/***************************************
 * MISC
 *****************************************/

function evaluateExpression(str: string) {
    const regex = /{(\w+|\d+)\s*([-+*/])\s*(\d+|\w+)}/;
    const match = regex.exec(str);
    if (match) {
        const tokenA = match[1];
        const operator = match[2];
        const tokenB = match[3];
        let numA = parseFloat(tokenA ?? "");
        if (isNaN(numA)) {
            numA = replaceToken(tokenA as Token);
        }
        let numB = parseFloat(tokenB ?? "");
        if (isNaN(numB)) {
            numB = replaceToken(tokenB as Token);
        }
        return operate(numA, operator ?? "", numB);
    }
    return null;
}

function replaceToken(token: Token) {
    switch (token) {
        case "year":
            return new Date().getFullYear();
        default:
            return 0;
    }
}

function operate(a: number, operator: string, b: number) {
    switch (operator) {
        case "+":
            return a + b;
        case "-":
            return a - b;
        case "*":
            return a * b;
        case "/":
            return a / b;
        default:
            return 0;
    }
}
